// Centralized pricing engine — the ONE place every purchase surface (PDP
// via assets/product-form.js, collection product cards/Product Carousel via
// assets/collection-product-card.js, Featured Product's pack-offer picker via
// assets/pack-offer-picker.js) computes what a cart line will actually cost.
// window.Velo.refreshAddToCartText (assets/quantity-selector.js) is the ONLY
// caller — every surface's own variant-resolution script just keeps the
// [data-add-to-cart] button's data-price/data-compare-at-price/
// data-is-single-variant/data-volume-pricing in sync with the currently
// selected variant and then calls refreshAddToCartText, instead of
// maintaining its own copy of this math.
//
// Loaded BEFORE assets/quantity-selector.js in layout/theme.liquid, since
// quantity-selector.js calls window.VeloPricing.calculate() from inside its
// own top-level IIFE (runs immediately once the script executes).
//
// Money in/out is always in the shop's minor currency unit (cents), matching
// Shopify's own Ajax Cart API and every other data-price attribute in this
// theme — NEVER the major-unit values stored in the custom.volume_pricing
// metafield (that one value is converted to cents internally, once, before
// use). This function is a client-side PREVIEW only; the real Discount
// Function (separate app repo, not this theme) is the actual checkout
// enforcement — see memory/docs for status.
window.Velo = window.Velo || {};
window.VeloPricing = window.VeloPricing || {};

// Moved here (from assets/quantity-selector.js) so it's available before
// window.VeloPricing.calculate needs it. Same pairs formula as the real
// Discount Function: pairs = floor(qty/2), remainder = qty%2,
// total = pairs*pairPrice + remainder*unitPrice. volumePricing is the
// custom.volume_pricing metafield's parsed JSON (MAJOR units per key, e.g.
// {"2":150,"3":200}) — only its "2" key (the pair price) is used; qty3/qty4/
// etc. are derived from it, never looked up as separate keys.
window.Velo.getVolumeTierTotalCents = function (volumePricing, quantity, unitPriceCents) {
  if (!volumePricing || typeof volumePricing !== 'object') return null;
  if (quantity < 2) return null;
  var tier2 = volumePricing['2'];
  if (typeof tier2 !== 'number' || !isFinite(tier2) || tier2 < 0) return null;
  var tier2Cents = Math.round(tier2 * 100);
  var pairs = Math.floor(quantity / 2);
  var remainder = quantity % 2;
  var tierCents = pairs * tier2Cents + remainder * unitPriceCents;
  var regularCents = unitPriceCents * quantity;
  if (tierCents >= regularCents) return null;
  return tierCents;
};

// Matches a real "Pack of N" option value (e.g. "Pack of 2"). Pack of N is a
// STOREFRONT PRICING OPTION ONLY — it is never submitted to Shopify as its
// own variant/SKU. Selecting it submits the product's Single variant with
// quantity × N (see window.Velo.resolvePackSubmission below), so Shopify
// orders, inventory, and Aramex only ever see the Single SKU.
window.VeloPricing.PACK_VALUE_PATTERN = /^pack of\s+(\d+)$/i;

// Option values reach the storefront already TRANSLATED on the Arabic
// locale ("Single" -> "قطعة واحدة", "Pack of 2" -> "عبوة من قطعتين"), so
// matching the English text alone silently turned Pack of N off for every
// Arabic shopper. These two helpers are the only place that knows the
// translated forms; everything else asks them.
//
// packSizeOf(value)   -> N for a "Pack of N" value, 0 for anything else
//                        (real pack variants such as "4 Bottles" are 0 too -
//                        they are sold as themselves).
// isSingleValue(value)-> true for the "Single" value.
window.VeloPricing.packSizeOf = function (value) {
  var text = String(value == null ? '' : value).trim();
  var match = window.VeloPricing.PACK_VALUE_PATTERN.exec(text);
  if (match) return parseInt(match[1], 10) || 0;
  if (/^عبوة من\s+قطعتين$/.test(text)) return 2;
  match = /^عبوة من\s+(\d+)/.exec(text);
  if (match) return parseInt(match[1], 10) || 0;
  return 0;
};

window.VeloPricing.isSingleValue = function (value) {
  var text = String(value == null ? '' : value).trim().toLowerCase();
  return text === 'single' || text === 'قطعة واحدة';
};

// Given the variant a customer's option selection resolves to, plus the full
// variants list, returns the variant that must actually be ADDED TO CART and
// the quantity multiplier that goes with it:
//
//   Single      -> {variant: Single,  multiplier: 1}
//   Pack of 2   -> {variant: Single,  multiplier: 2}
//
// The Single variant is found by taking the selected variant's own option
// values and swapping the "Pack of N" value for "Single" (all other option
// positions unchanged), so multi-option products (e.g. Colour + Pack Size)
// still resolve to the correct Single SKU. Falls back to the selected
// variant unchanged when there is no pack value or no matching Single.
window.Velo.resolvePackSubmission = function (variant, variants) {
  var fallback = { variant: variant, multiplier: 1 };
  if (!variant || !variant.options || !variants || !variants.length) return fallback;

  var packIndex = -1;
  var packSize = 1;
  variant.options.forEach(function (value, index) {
    var size = window.VeloPricing.packSizeOf(value);
    if (size) {
      packIndex = index;
      packSize = size;
    }
  });
  if (packIndex === -1 || packSize < 2) return fallback;

  var singleVariant = variants.find(function (candidate) {
    if (!candidate.options || candidate.options.length !== variant.options.length) return false;
    return candidate.options.every(function (value, index) {
      if (index === packIndex) return window.VeloPricing.isSingleValue(value);
      return value === variant.options[index];
    });
  });
  if (!singleVariant) return fallback;

  return { variant: singleVariant, multiplier: packSize };
};

// window.VeloPricing.calculate({selectedVariant, quantity, isSingleVariant, volumePricing, packMultiplier})
//
// selectedVariant: {id, price, compare_at_price} — price/compare_at_price in
//   cents, always the REAL Shopify variant that will be added to the cart.
//   In Pack of N mode that is the SINGLE variant (its per-unit price), never
//   a separate pack variant.
// quantity: the DISPLAYED quantity from the shared stepper
//   (snippets/quantity-selector.liquid) — "how many Singles" or "how many
//   Packs", exactly what the customer sees.
// packMultiplier: units per displayed quantity — 1 for Single, N for
//   "Pack of N". physicalQuantity = quantity × packMultiplier is what is
//   actually submitted to /cart/add.js.
// isSingleVariant: whether the variant being added is the product's "Single"
//   option value (snippets/is-single-variant.liquid) — volume pricing only
//   ever applies to this case (which now includes every Pack of N selection,
//   since those add the Single variant).
// volumePricing: parsed custom.volume_pricing metafield object, or null.
//
// Returns pricingMode ('single_regular' | 'single_volume' | 'plain_variant'),
// variantId, cartQuantity (= physicalUnits = displayQuantity ×
// packMultiplier, exactly what gets POSTed to /cart/add.js),
// finalTotal/originalTotal/savings (cents), hasDiscount.
window.VeloPricing.calculate = function (input) {
  input = input || {};
  var selectedVariant = input.selectedVariant || {};
  var isSingleVariant = !!input.isSingleVariant;
  var volumePricing = input.volumePricing || null;

  var displayQuantity = Math.max(1, parseInt(input.quantity, 10) || 1);
  var packMultiplier = Math.max(1, parseInt(input.packMultiplier, 10) || 1);
  var quantity = displayQuantity * packMultiplier;
  var unitPriceCents = parseInt(selectedVariant.price, 10) || 0;
  var compareAtCents = parseInt(selectedVariant.compare_at_price, 10) || 0;
  var regularTotalCents = unitPriceCents * quantity;

  var pricingMode = 'single_regular';
  var finalTotal = regularTotalCents;
  var originalTotal = regularTotalCents;

  if (isSingleVariant) {
    var tierTotalCents = window.Velo.getVolumeTierTotalCents(volumePricing, quantity, unitPriceCents);
    if (tierTotalCents !== null) {
      pricingMode = 'single_volume';
      finalTotal = tierTotalCents;
      originalTotal = regularTotalCents;
    } else if (compareAtCents > unitPriceCents) {
      originalTotal = compareAtCents * quantity;
    }
  } else {
    // Any real variant that is not the product's "Single" option value (e.g.
    // a plain colour-only product): its OWN price/compare_at_price ×
    // quantity — never the volume_pricing metafield. Pack of N no longer
    // reaches this branch: it submits the Single variant with a multiplier,
    // so it is always isSingleVariant === true.
    pricingMode = 'plain_variant';
    if (compareAtCents > unitPriceCents) {
      originalTotal = compareAtCents * quantity;
    }
  }

  var hasDiscount = originalTotal > finalTotal;
  var savings = hasDiscount ? originalTotal - finalTotal : 0;
  // `quantity` is ALREADY the physical quantity (displayQuantity ×
  // packMultiplier) — never multiply by the pack size a second time.
  var physicalUnits = quantity;

  return {
    pricingMode: pricingMode,
    variantId: selectedVariant.id,
    cartQuantity: quantity,
    physicalUnits: physicalUnits,
    finalTotal: finalTotal,
    originalTotal: hasDiscount ? originalTotal : finalTotal,
    savings: savings,
    hasDiscount: hasDiscount
  };
};
